import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Bot, Shield, LineChart } from 'lucide-react';

interface ProblemCategory {
  id: string;
  title: string;
  description: string;
  problemCount: number;
  icon: 'ai' | 'security' | 'business';
}

interface ProblemCategoryCardProps {
  category: ProblemCategory;
  index: number;
}

const CardContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '350px',
  height: '400px',
  margin: '0 auto',
  background: '#fff',
  boxShadow: '0 15px 60px rgba(0,0,0, 0.5)',
  borderRadius: '15px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    '& .face2': {
      height: '60px',
      borderRadius: '0 0 15px 15px',
      '& h2': {
        fontSize: '2em',
      },
    },
  },
}));

const Face1 = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '100%',
  padding: '20px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(5px)',
}));

const Face2 = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(45deg, #3503ad, #f7308c)',
  transition: '0.5s',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '50%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '15px 0 0 15px',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  background: 'rgba(138, 43, 226, 0.1)',
  border: '2px solid rgba(138, 43, 226, 0.2)',
}));

const ProblemCategoryCard: React.FC<ProblemCategoryCardProps> = ({ category, index }) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'ai':
        return <Bot size={40} color="#8a2be2" />;
      case 'security':
        return <Shield size={40} color="#8a2be2" />;
      case 'business':
        return <LineChart size={40} color="#8a2be2" />;
      default:
        return null;
    }
  };

  const getGradient = (index: number) => {
    switch (index) {
      case 0:
        return 'linear-gradient(45deg, #3503ad, #f7308c)';
      case 1:
        return 'linear-gradient(45deg, #8a2be2, #f42f8c)';
      case 2:
        return 'linear-gradient(45deg, #6a0dad, #ff69b4)';
      default:
        return 'linear-gradient(45deg, #3503ad, #f7308c)';
    }
  };

  return (
    <CardContainer>
      <Face1 className="face1">
        <IconContainer>
          {getIcon(category.icon)}
        </IconContainer>
        <Typography 
          variant="h5" 
          sx={{ 
            color: '#8a2be2',
            textShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
            mb: 2,
            fontWeight: 500
          }}
        >
          {category.title}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 2
          }}
        >
          {category.description}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#8a2be2',
            fontWeight: 500
          }}
        >
          {category.problemCount} problems
        </Typography>
      </Face1>
      <Face2 
        className="face2"
        sx={{
          background: getGradient(index),
        }}
      >
        <Typography 
          variant="h2" 
          sx={{ 
            fontSize: '10em',
            color: '#fff',
            transition: '0.5s',
            textShadow: '0 2px 5px rgba(0,0,0, 0.2)',
          }}
        >
          {(index + 1).toString().padStart(2, '0')}
        </Typography>
      </Face2>
    </CardContainer>
  );
};

export default ProblemCategoryCard; 